<template>
  <modal name="update-alarm-center" class="modal-inner modal-update-alarm-center" transition="pop-out" :width="modalWidth" :focus-trap="true" :min-height="600" height="auto" :scrollable="true" :resizable="true" :adaptive="true" :reset="true" @before-close="resetForm">
    <span class="close-button" @click="hide('update-alarm-center')"><BaseIcon icon="times-circle" class="text-white"/></span>
    <div class="modal-header text-lg bg-primary-50 font-serif">
      <span v-if="updateInfo">{{ updateInfo.name }}</span>
    </div>
    <div class="modal-body">
      <ValidationObserver ref="formUpdateAlarmCenter" v-if="updateInfo != null">
        <div class="flex flex-wrap">
          <div class="w-full md:w-1/3 px-2 py-2">
            <BaseInput v-model="updateInfo.name" type="text" :field_name="$t('alarm_center_update.name')" rules="required" />
          </div>
          <div class="w-full md:w-1/3 px-2 py-2">
            <BaseInput v-model="updateInfo.phone" type="text" :field_name="$t('alarm_center_update.phone')" rules="required" />
          </div>
          <div class="w-full md:w-1/3 px-2 py-2">
            <BaseSelect v-model="updateInfo.communication_language" :options="communicationLanguages" nameSelector="name" optionSelector="id" :field_name="$t('communication_language.label')" rules="required" />
            <span class="text-xs italic">{{ $t('communication_language.hint') }}</span>
          </div>
        </div>

        <div class="flex flex-wrap">
          <div class="w-full md:w-1/3 px-2 py-2">
            <BaseInput v-model="updateInfo.email" type="text" :field_name="$t('alarm_center_update.email')" rules="required" />
            <span class="text-xs italic">{{ $t('alarm_center_update.email_multiple_hint') }}</span>
          </div>
        </div>

        <div class="mx-2 border-b border-secondary-400 my-5"></div>

        <div class="flex flex-wrap">
          <div class="w-full flex-col px-2 py-2">
            <div class="w-full text-gray-600 text-sm font-bold pb-2 font-serif">{{ $t('alarm_center_update.sms') }} <b class="text-xxs italic font-normal font-sans text-secondary-500">({{ $t('alarm_center_update.sms_full_number_hint') }})</b></div>
            <div class="w-full flex flex-wrap mt-2" v-for="(n, i) in updateInfo.report_phones" :key="i">
              <div class="w-2/12 pr-2">
                <BaseSelect v-model="n.order" :options="[1, 2, 3, 4]" :field_name="$t('alarm_center_update.priority_order') + ' ' + (i + 1)" rules="required" />
              </div>
              <div class="w-3/12 pr-2">
                <BaseInput v-model="n.msisdn" @keypress="isNumber($event)" maxlength="15" type="text" :field_name="$t('alarm_center_update.sms_number') + ' ' + (i + 1)" rules="required" />
              </div>
              <div class="w-2/12 pr-2">
                <BaseSelect v-model="n.device_identifier" :options="deviceIdentifiers" nameSelector="name" optionSelector="id" :field_name="$t('alarm_center_update.device_identifier') + ' ' + (i + 1)" rules="required" />
              </div>
              <div class="w-2/12 pr-2">
                <BaseSelect v-model="n.report_format" :options="reportFormats" nameSelector="name" optionSelector="id" :field_name="$t('alarm_center_update.report_format') + ' ' + (i + 1)" rules="required" />
              </div>
              <div class="w-2/12 flex flex-col pr-2 pt-2">
                <template v-for="s in additionalSettings">
                  <div class="w-full" v-bind:key="s.id">
                    <label class="inline-flex items-center mt-2">
                      <input v-model="n.additional_settings" :value="s.id" type="checkbox" class="form-checkbox h-3 w-3" />
                      <span class="ml-2 text-gray-600 text-sm font-sans font-semibold">{{ s.name }}</span>
                    </label>
                  </div>
                </template>
              </div>
              <div class="w-1/12 pt-8 text-center">
                <button class="text-red-500 pl-2" @click.prevent="removeSMSInput(i)"><BaseIcon icon="minus-circle" /></button>
              </div>
            </div>
            <div class="w-full">
              <button class="btn-blue-outline mt-2" @click.prevent="addSMSInput()">{{ $t('alarm_center_update.add') }} <BaseIcon icon="plus" class="ml-1" /></button>
            </div>
          </div>
        </div>

        <div class="mx-2 border-b border-secondary-400 my-5"></div>

        <div class="flex flex-wrap">
          <div class="w-full flex-col px-2 py-2">
            <div class="w-full text-gray-600 text-sm font-bold pb-2 font-serif">{{ $t('alarm_center_update.tcp') }}</div>
            <div class="w-full flex flex-wrap mt-2" v-for="(n, i) in updateInfo.report_ips" :key="i">
              <div class="w-2/12 pr-2">
                <BaseSelect v-model="n.order" :options="[1, 2, 3, 4]" :field_name="$t('alarm_center_update.priority_order') + ' ' + (i + 1)" rules="required" />
              </div>
              <div class="w-3/12 pr-2">
                <BaseInput v-model="n.ip" maxlength="255" type="text" :field_name="$t('alarm_center_update.ip_address') + ' ' + (i + 1)" rules="required" />
              </div>
              <div class="w-2/12 pr-2">
                <BaseInput v-model="n.port" @keypress="isNumber($event)" min="0" max="65535" placeholder="Port" type="number" :field_name="$t('alarm_center_update.port') + ' ' + (i + 1)" rules="required" />
              </div>
              <div class="w-2/12 pr-2">
                <BaseSelect v-model="n.device_identifier" :options="deviceIdentifiers" nameSelector="name" optionSelector="id" :field_name="$t('alarm_center_update.device_identifier_tcp') + ' ' + (i + 1)" rules="required" />
              </div>
              <div class="w-2/12 pr-2">
                <BaseSelect v-model="n.report_format" :options="reportFormats" nameSelector="name" optionSelector="id" :field_name="$t('alarm_center_update.report_format_tcp') + ' ' + (i + 1)" rules="required" />
              </div>
              <div class="w-1/12 pt-8 text-center">
                <button class="text-red-500 pl-2" @click.prevent="removeIPInput(i)"><BaseIcon icon="minus-circle" /></button>
              </div>
            </div>
            <div class="w-full">
              <button class="btn-blue-outline mt-2" @click.prevent="addIPInput()">{{ $t('alarm_center_update.add') }} <BaseIcon icon="plus" class="ml-1" /></button>
            </div>
          </div>
        </div>

        <div class="mx-2 border-b border-secondary-400 my-5"></div>

        <div class="flex flex-wrap">
          <div class="w-full md:w-1/3 flex-col px-2 py-2">
            <div class="w-full text-gray-600 text-sm font-bold pb-2 font-serif">{{ $t('alarm_center_update.report_type') }}</div>
            <div class="w-full" v-for="r in reportTypes" :key="r.id">
              <label class="inline-flex items-center mt-3">
                <input v-model="updateInfo.report_types" :value="r.id" type="checkbox" class="form-checkbox h-4 w-4" /><span class="ml-2 text-sm font-sans font-semibold">{{ r.name }}</span>
              </label>
            </div>
          </div>
        </div>

        <div class="mx-2 border-b border-secondary-400 my-5"></div>

        <div class="flex flex-wrap">
          <div class="w-full md:w-1/3 px-2 py-2">
            <label class="inline-flex items-center mt-3"> <input v-model="updateInfo.periodic_report.active" type="checkbox" class="form-checkbox h-4 w-4" /><span class="ml-2 text-sm font-sans font-semibold">{{ $t('alarm_center_update.periodic_report') }}</span> </label>
          </div>
          <div v-if="updateInfo.periodic_report && updateInfo.periodic_report.active" class="w-full md:w-1/3 px-2 py-2">
            <BaseSelect v-model="updateInfo.periodic_report.trigger" :options="periodicReportTriggers" nameSelector="name" optionSelector="id" :field_name="$t('alarm_center_update.trigger')" rules="required" />
          </div>
          <div v-if="updateInfo.periodic_report && updateInfo.periodic_report.active && updateInfo.periodic_report.trigger == 'critical'" class="w-full md:w-1/3 px-2 py-2">
            <BaseInput v-model="updateInfo.periodic_report.time_limit" @keypress="isNumber($event)" min="0" max="120" placeholder="Minutes" type="number" :field_name="$t('alarm_center_update.time')" rules="required" />
          </div>
        </div>
        <div class="mx-2 border-b border-secondary-400 my-5"></div>

        <div class="flex flex-wrap" v-if="isNorwegianSite">
          <div class="w-full md:w-1/3 px-2 py-2">
            <label class="inline-flex items-center mt-3">
              <input v-model="updateInfo.guard_dispatch" type="checkbox" class="form-checkbox h-4 w-4" />
              <span class="ml-2 text-sm font-sans font-semibold">{{ $t('alarm_center_update.guard_dispatch') }}</span>
            </label>
            <p class="text-xs text-gray-500 mt-1 ml-6">{{ $t('alarm_center_update.guard_dispatch_hint') }}</p>
          </div>
        </div>

        <div class="mx-2 border-b border-secondary-400 my-5"></div>

        <div class="flex flex-wrap">
          <div class="w-full md:w-1/3 px-2 py-2">
            <div class="w-full text-gray-600 text-xl font-bold pb-2 font-serif px-2">
              {{ $t('alarm_center_update.alarm_center_view') }}
            <b class="text-xxs italic font-normal font-sans text-secondary-500">{{ $t('alarm_center_update.alarm_center_view_hint') }}</b>
          </div>
            <div class="w-full md:w-2/3 px-2 py-2">
              <BaseSelect v-model="updateInfo.alarm_presentation_options" :options="alarmShowOptions" nameSelector="name" optionSelector="id" :field_name="$t('alarm_center_update.display_mode')" rules="required" />
            </div>
          </div>
        </div>

 

        <div class="flex flex-wrap">
          <div class="w-full text-gray-600 text-sm font-bold pb-2 font-serif px-4">
            {{ $t('alarm_center_update.instructions_by_risk_profile') }}
            <b class="text-xxs italic font-normal font-sans text-secondary-500">{{ $t('alarm_center_update.instructions_hint') }}</b>
          </div>
          <div class="w-full px-2 py-2">
            <h2 class="text-gray-600 text-sm font-bold pb-2 font-serif px-2">{{ $t('alarm_center_update.threat_alarm') }}</h2>
            <TextEditor rows="10" v-model="updateInfo.threat_instruction" class="h-96 overflow-y-auto"/>
          </div>
          <div class="w-full px-2 py-2">
            <h2 class="text-gray-600 text-sm font-bold pb-2 font-serif px-2">{{ $t('alarm_center_update.emergency_alarm') }}</h2>
            <TextEditor rows="10" v-model="updateInfo.emergency_instruction" class="h-96 overflow-y-auto"/>
          </div>
          <div class="w-full px-2 py-2">
            <h2 class="text-gray-600 text-sm font-bold pb-2 font-serif px-2">{{ $t('alarm_center_update.assault_alarm') }}</h2>
            <TextEditor rows="10" v-model="updateInfo.assault_instruction" class="h-96 overflow-y-auto"/>
          </div>
        </div>

      </ValidationObserver>
    </div>
    <div class="modal-footer">
      <div class="footer-container clearfix">
        <button class="btn-red-outline px-3 py-2 float-left" @click="hide('update-alarm-center')">{{ $t('alarm_center_update.cancel') }}<BaseIcon icon="times-circle" class="ml-1" /></button>
        <button class="btn-green px-3 py-2 float-right" @click="updateAlarmCenter()">{{ $t('alarm_center_update.save') }}<BaseIcon icon="save" class="ml-1" /></button>
      </div>
    </div>
  </modal>
</template>

<script>
import TextEditor from "@/components/text_editor/editor.vue";
import * as siteContext from "@/utils/siteContext";
import { communicationLanguageOptions, communicationLanguageOf } from "@/utils/alarmCenterLanguage";
const MODAL_WIDTH = 1300;

export default {
  siteContext,
  components: {
    TextEditor,
  },
  props: {
    alarmCenter: {
      required: true,
      default: null,
    },
  },

  data() {
    return {
      reportFormats: [
        { name: this.$t('create_alarm_central_page.report_format_ovls_bm'), id: "ovls_bm" },
        { name: this.$t('create_alarm_central_page.report_format_srt'), id: "srt" },
        { name: this.$t('create_alarm_central_page.report_format_shortxml'), id: "shortxml" },
        { name: this.$t('create_alarm_central_page.report_format_signal_proto'), id: "signal_proto" },
        { name: this.$t('create_alarm_central_page.report_format_ovls_tempest'), id: "ovls_tempest" },
        { name: this.$t('create_alarm_central_page.report_format_ovls_avarn'), id: "ovls_avarn" },
        { name: this.$t('create_alarm_central_page.report_format_mptp'), id: "mptp" },
        { name: this.$t('create_alarm_central_page.report_format_avarn_json'), id: "avarn_json" },
        { name: this.$t('create_alarm_central_page.report_format_user_defined'), id: "user_defined" },
        { name: "SIA-Safe-4", id: "sia" }, // SIA DC-09 — used to tunnel alarms to Safe-4 (SEC-210)
      ],
      user: this.$store.state.user,
      updateInfo: null,
    };
  },

  computed: {
    isNorwegianSite() {
      return this.$options.siteContext.isNorwegianSite();
    },
    communicationLanguages() {
      return communicationLanguageOptions(this.$t.bind(this));
    },
    reportTypes() {
      return [
        { name: "Push Button", id: "push_button" },
        { name: this.$t('secure_tool.man_down'), id: "man_down" },
        { name: this.$t('secure_tool.timer_alarm'), id: "timer_alarm_stop" },
        { name: this.$t('secure_tool.internal_alarm'), id: "call_b" },
      ];
    },
    deviceIdentifiers() {
      return [
        { name: "IMEI", id: "imei_number" },
        { name: this.$t('alarm_create.telematic_number'), id: "phone" },
        { name: this.$t('alarm_create.alarm_number'), id: "alarm_number" },
      ];
    },
    additionalSettings() {
      return [
        { name: this.$t('alarm_center_update.google_map_link'), id: "gmap_link" },
        { name: this.$t('alarm_center_update.rf_securpos'), id: "tag_address" },
      ];
    },
    periodicReportTriggers() {
      return [
        { name: this.$t('alarm_create.all'), id: "always" },
        { name: this.$t('secure_tool.live_alarm_tracking') + ', ' + this.$t('secure_tool.man_down'), id: "critical" },
      ];
    },
    alarmShowOptions() {
      return [
        { name: this.$t('alarm_center_menu.show_all_alarms'), id: "showAll" },
        { name: this.$t('alarm_center_menu.show_only_active_event'), id: "showOnlyEvent" },
      ];
    },
  },

  watch: {
    alarmCenter(newVal) {
      // eslint-disable-line
      this.updateInfo = _.cloneDeep(newVal);
      // an older centre has no language yet: show the one it is mailed in today
      if (this.updateInfo && !this.updateInfo.communication_language) {
        this.$set(this.updateInfo, "communication_language", communicationLanguageOf(newVal));
      }
    },
  },

  methods: {
    updateAlarmCenter() {
      this.$refs.formUpdateAlarmCenter.validate().then((success) => {
        if (!success) {
          return;
        }

        // validate IP address
        let isValid = this.validateIPaddresses(this.updateInfo.report_ips);
        if (isValid == false) {
          this.handleError(null, "IP-adress är ogiltig");
          return;
        }

        let emails = this.updateInfo.email.replace(/\s+/g, "").split(",");
        let emails_filtered = [];
        emails.forEach((e) => {
          if (this.validateEmail(e)) emails_filtered.push(e);
        });
        emails_filtered = emails_filtered.join();

        let data = {
          name: this.updateInfo.name,
          email: emails_filtered,
          phone: this.updateInfo.phone,
          communication_language: this.updateInfo.communication_language,
          report_phones: this.updateInfo.report_phones,
          report_ips: this.updateInfo.report_ips,
          report_types: this.updateInfo.report_types,
          device_identifier: this.updateInfo.device_identifier,
          google_map_link: this.updateInfo.google_map_link,
          periodic_report: this.updateInfo.periodic_report,
          threat_instruction: (this.updateInfo.threat_instruction || '').trim(),
          emergency_instruction: (this.updateInfo.emergency_instruction || '').trim(),
          assault_instruction: (this.updateInfo.assault_instruction || '').trim(),
          alarm_presentation_options: this.updateInfo.alarm_presentation_options,
          guard_dispatch: this.updateInfo.guard_dispatch || false
        };

        this.axios
          .put(`${process.env.VUE_APP_SERVER_URL}/alarm-central/${this.updateInfo._id}`, data)
          .then(() => {
            // eslint-disable-line
            this.$emit("updateAlarmCenter");
            this.$modal.hide("update-alarm-center");
          })
          .catch((error) => {
            this.handleError(error);
          });
      });
    },

    validateEmail: function(e) {
      //eslint-disable-next-line
      let emailRegex = new RegExp(/^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/);
      return emailRegex.test(e);
    },

    validateIPaddresses(ipAddresses) {
      let ipRegex = /^(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$/;
      let domainRegex = /^([a-zA-Z0-9]([a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?\.)+[a-zA-Z]{2,}$/;
      let isValid = true;
      ipAddresses.forEach((a) => {
        if (!ipRegex.test(a.ip) && !domainRegex.test(a.ip)) isValid = false;
      });

      return isValid;
    },

    addSMSInput() {
      this.updateInfo.report_phones.push({ msisdn: "", device_identifier: "", report_format: "", additional_settings: [] });
    },

    removeSMSInput(i) {
      this.updateInfo.report_phones.splice(i, 1);
    },

    addIPInput() {
      this.updateInfo.report_ips.push({ ip: "", port: "", device_identifier: "", report_format: "" });
    },

    removeIPInput(i) {
      this.updateInfo.report_ips.splice(i, 1);
    },

    isNumber: function(evt) {
      evt = evt ? evt : window.event;
      var charCode = evt.which ? evt.which : evt.keyCode;
      if (charCode > 31 && (charCode < 48 || charCode > 57)) {
        evt.preventDefault();
      } else {
        return true;
      }
    },

    resetForm() {
      this.updateInfo = null;
      this.$refs.formUpdateAlarmCenter.reset();
    },

    hide(id) {
      this.resetForm();
      this.$modal.hide(id);
    },
  },

  created() {
    this.modalWidth = window.innerWidth < MODAL_WIDTH ? window.innerWidth : MODAL_WIDTH;
  },
};
</script>
